import React, { useState, useEffect } from 'react';
import { History, Zap } from 'lucide-react';
import { HistoryItem } from '@/types';
import { supabase } from '@/lib/supabase';
import { Session } from '@supabase/supabase-js';
import { AuthModal } from '@/components/AuthModal';
import { BackgroundBlobs } from '@/components/BackgroundBlobs';
import { Navbar } from '@/components/Navbar';
import { Hero } from '@/components/Hero';
import { OffersCarousel } from '@/components/OffersCarousel';
import { RegulationModal } from '@/components/modals/RegulationModal';
import { GeneratorSection } from '@/components/GeneratorSection';
import { HistoryList } from '@/components/HistoryList';
import { InstallPromptBanner } from '@/components/InstallPromptBanner';
import { Community } from '@/components/Community';
import { ScrollToTop } from '@/components/ScrollToTop';
import { LinkRedirect } from '@/components/LinkRedirect';
import { LevelInfoModal } from '@/components/modals/LevelInfoModal';
import { PromoBannersCarousel } from '@/components/PromoBannersCarousel';
import { RewardsStoreModal } from '@/components/modals/RewardsStoreModal';
import { PointsEarningRequestModal } from '@/components/modals/PointsEarningRequestModal';
import { PointsEarningHistoryModal } from '@/components/modals/PointsEarningHistoryModal';
import { checkIntegrations } from '@/services/IntegrationService';
import { registerDailyLogin, shouldShowBirthdayModal } from '@/services/LoyaltyService';
import { RadarLevel } from '@/types';
import { BirthdayBonusModal } from '@/components/modals/BirthdayBonusModal';
import { ReferralModal } from '@/components/modals/ReferralModal';

const App: React.FC = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isRegulationOpen, setIsRegulationOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'generate' | 'history'>('generate');
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [isOffersExpanded, setIsOffersExpanded] = useState(true);
  const [isLevelInfoOpen, setIsLevelInfoOpen] = useState(false);
  const [isStoreOpen, setIsStoreOpen] = useState(false);
  const [isPointsRequestOpen, setIsPointsRequestOpen] = useState(false);
  const [isPointsHistoryOpen, setIsPointsHistoryOpen] = useState(false);
  const [isReferralOpen, setIsReferralOpen] = useState(false);
  const [isBirthdayModalOpen, setIsBirthdayModalOpen] = useState(false);
  const [userLevel, setUserLevel] = useState<RadarLevel>('bronze');

  // Simple Router Logic
  const pathname = window.location.pathname;
  if (pathname.startsWith('/r/')) {
    const shortCode = pathname.replace('/r/', '');
    return <LinkRedirect shortCode={shortCode} />;
  }

  // Fetch Level and Birthday
  useEffect(() => {
    if (session?.user) {
      // Fetch level and birthdate
      supabase.from('profiles').select('level, birthdate').single().then(({ data }) => {
        if (data) {
          if (data.level) setUserLevel(data.level as RadarLevel);
          if (data.birthdate) {
            const showModal = shouldShowBirthdayModal(data.birthdate, session.user.id);
            if (showModal) {
              // Fetch from Database to confirm if user has claimed the reward
              supabase
                .from('loyalty_user_actions')
                .select('*')
                .eq('action', 'birthday_bonus')
                .eq('period_key', new Date().getFullYear())
                .maybeSingle()
                .then(({ data }) => {
                  if (!data) {
                    setIsBirthdayModalOpen(true);
                  }
                });
            }
          }
        }
      });
    } else {
      setUserLevel('bronze');
    }
  }, [session?.user?.id]);

  // Loyalty Check (Daily Login)
  useEffect(() => {
    if (session?.user) {
      registerDailyLogin(session.user.id);
    }
  }, [session]);

  // Mobile Detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle Query Parameters (Auto-Open Auth)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      localStorage.setItem('referral_code', ref);
    }
    if (params.get('login') === 'true') {
      setIsAuthOpen(true);
    }
  }, []);

  // Auth State
  const lastCheckedToken = React.useRef<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.access_token && session.access_token !== lastCheckedToken.current) {
        lastCheckedToken.current = session.access_token;
        checkIntegrations(session.access_token);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      if (session?.access_token && session.access_token !== lastCheckedToken.current) {
        lastCheckedToken.current = session.access_token;
        checkIntegrations(session.access_token);
      }

      // Automatically redirect the user back if returnUrl is present upon login
      if (event === 'SIGNED_IN') {
        const params = new URLSearchParams(window.location.search);
        const returnUrl = params.get('returnUrl');
        if (returnUrl && returnUrl.startsWith('/')) {
          window.location.href = returnUrl;
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Theme State Initialization
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme');
      if (saved === 'dark' || saved === 'light') return saved;
      return 'light';
    }
    return 'light';
  });

  // Load History
  useEffect(() => {
    const loadHistory = async () => {
      if (session?.user) {
        // Fetch from Supabase when logged in
        const { data, error } = await supabase
          .from('affiliate_links')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(50);

        if (error) {
          console.error('Error fetching history:', error);
        } else if (data) {
          const mappedHistory: HistoryItem[] = data.map(item => ({
            id: item.id,
            originalUrl: item.original_url,
            shortUrl: `${import.meta.env.VITE_BASE_URL || 'https://radardaeconomia.com.br'}/r/${item.short_code}`,
            platform: item.provider,
            date: item.created_at
          }));
          setHistory(mappedHistory);
        }
      } else {
        // Fetch from LocalStorage when logged out
        const savedHistory = localStorage.getItem('link_history');
        if (savedHistory) {
          try {
            setHistory(JSON.parse(savedHistory));
          } catch (e) {
            console.error("Failed to parse history", e);
          }
        } else {
          setHistory([]);
        }
      }
    };

    loadHistory();
  }, [session?.user?.id]);

  // Apply Theme
  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const addToHistory = (item: HistoryItem) => {
    const newHistory = [item, ...history].slice(0, 50); // Limit to 50 items
    setHistory(newHistory);

    // Only save to localStorage if user is NOT logged in
    if (!session?.user) {
      localStorage.setItem('link_history', JSON.stringify(newHistory));
    }
  };

  const clearHistory = async () => {
    setHistory([]);

    if (!session?.user) {
      localStorage.removeItem('link_history');
    }
    // If logged in, we are currently only clearing the local view as per request to "retrieve"
    // To implement delete in DB, we would add the delete call here.
  };

  return (
    <div className="relative min-h-screen font-sans overflow-hidden bg-gray-50 dark:bg-[#0a0a0c] text-gray-900 dark:text-white transition-colors duration-500">

      <BackgroundBlobs />

      <Navbar
        session={session}
        onAuthOpen={() => setIsAuthOpen(true)}
        theme={theme}
        toggleTheme={toggleTheme}
        isMobile={isMobile}
      />

      <main className="relative z-10 pt-2 pb-12 px-4 flex flex-col items-center justify-center">

        <div className="w-full mt-8">
          <PromoBannersCarousel
            isLoggedIn={!!session?.user}
            onActionClick={(index) => {
              if (!session?.user) {
                setIsAuthOpen(true);
                return;
              }
              if (index === 0) setIsLevelInfoOpen(true);
              if (index === 1) setIsStoreOpen(true);
              if (index === 2) setIsPointsRequestOpen(true);
              if (index === 3) setIsReferralOpen(true);
            }}
          />
        </div>

        <Hero />

        {/* Dynamic Card Container */}
        <div className={`w-full transition-all duration-500 ease-out ${isOffersExpanded ? 'max-w-7xl' : 'max-w-xl'}`}>

          {isMobile && <InstallPromptBanner />}

          {/* Navigation Tabs */}
          <div className="flex justify-center mb-6">
            <div className="p-1.5 glass-card rounded-2xl flex gap-1 border border-white/40 dark:border-gray-700/50 shadow-sm">
              <button
                onClick={() => setActiveTab('generate')}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === 'generate'
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
              >
                <Zap className="w-4 h-4" />
                Otimizador
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === 'history'
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
              >
                <History className="w-4 h-4" />
                Histórico
              </button>
            </div>
          </div>

          {activeTab === 'generate' ? (
            <GeneratorSection
              session={session}
              addToHistory={addToHistory}
              isMobile={isMobile}
              onAuthOpen={() => setIsAuthOpen(true)}
            />
          ) : (
            <HistoryList
              history={history}
              onClear={clearHistory}
              isMobile={isMobile}
              isLoggedIn={!!session?.user}
            />
          )}

          <Community />

          <OffersCarousel
            session={session}
            onAuthOpen={() => setIsAuthOpen(true)}
            isExpanded={isOffersExpanded}
            onToggleExpand={() => setIsOffersExpanded(!isOffersExpanded)}
          />
        </div>

        {/* Footer info */}
        <div className="mt-12 text-center opacity-60 hover:opacity-100 transition-opacity flex flex-col items-center gap-2">
          <p className="text-[10px] font-semibold tracking-widest uppercase text-gray-500">
            Powered by Radar da Economia @ 2026
          </p>
          <button
            onClick={() => setIsRegulationOpen(true)}
            className="text-[10px] text-gray-400 hover:text-blue-500 underline transition-colors"
          >
            Regulamento
          </button>
        </div>
      </main>

      <AuthModal
        isOpen={isAuthOpen}
        onClose={() => setIsAuthOpen(false)}
        onOpenRegulation={() => setIsRegulationOpen(true)}
      />

      <RegulationModal
        isOpen={isRegulationOpen}
        onClose={() => setIsRegulationOpen(false)}
      />

      <LevelInfoModal
        isOpen={isLevelInfoOpen}
        onClose={() => setIsLevelInfoOpen(false)}
        currentLevel={userLevel}
        levelExpiresAt={null}
      />

      {session && isStoreOpen && (
        <RewardsStoreModal
          session={session}
          isOpen={isStoreOpen}
          onClose={() => setIsStoreOpen(false)}
          onOpenHistory={() => { }}
        />
      )}

      {session && isPointsRequestOpen && (
        <PointsEarningRequestModal
          session={session}
          isOpen={isPointsRequestOpen}
          onClose={() => setIsPointsRequestOpen(false)}
          onViewRequests={() => {
            setIsPointsRequestOpen(false);
            setIsPointsHistoryOpen(true);
          }}
        />
      )}

      {session && isPointsHistoryOpen && (
        <PointsEarningHistoryModal
          isOpen={isPointsHistoryOpen}
          onClose={() => setIsPointsHistoryOpen(false)}
        />
      )}

      {session && isReferralOpen && (
        <ReferralModal
          session={session}
          isOpen={isReferralOpen}
          onClose={() => setIsReferralOpen(false)}
        />
      )}

      {session && isBirthdayModalOpen && (
        <BirthdayBonusModal
          isOpen={isBirthdayModalOpen}
          session={session}
          onClose={() => setIsBirthdayModalOpen(false)}
          onSuccess={() => {
            setIsBirthdayModalOpen(false);
            const year = new Date().getFullYear();
            localStorage.setItem(`birthday_claimed_${session.user.id}_${year}`, 'true');
          }}
        />
      )}

      <ScrollToTop />
    </div>
  );
};

export default App;